/**
 * 🕯️ TombOfAwakeningPage — Scene I of Act 1, canon intro.
 *
 * Canon: /app/memory/TOMB_OF_AWAKENING_SCENE1_CANON_FEB2026.md
 *
 * Walkable single-screen scene. Fixed camera. Tap/click or WASD to move.
 * 5-beat tutorial state machine narrated by ??? (secretly Mother Phoenix
 * — do NOT reveal in dialogue). Ends with +500 Chit reward + exit.
 *
 * MVP background: CSS-painted (cold stone gradient + moonlight beam +
 * pulsing runes). Real painterly Nano Banana render is a v2 swap.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const SCENE_W = 900;
const SCENE_H = 560;
const SPRITE = 36;
const WALK_STEP = 8;    // px per keydown
const CLICK_SPEED = 3;  // px per animation frame toward click target

// Character spawn (center of chamber, slightly forward from wake spot)
const SPAWN = { x: 200, y: 380 };
// Anchors in the scene
const RUNE = { x: 90, y: 210, r: 40 };
const CONSTRUCT = { x: 640, y: 300, r: 50 };
// Where the shadow lurks, then bolts toward EXIT — the two coins
// he was carrying land on his flight-arc (canon: he fumbled BOTH).
const SHADOW_START = { x: 720, y: 180 };
const BAG        = { x: 470, y: 340, r: 30 }; // Blue-Coin bag — mid-arc
const FLAME_COIN = { x: 660, y: 250, r: 26 }; // Flame Coin — later arc, closer to exit
const EXIT = { x: 810, y: 260, w: 80, h: 200 };

// 🕯️ Canon amendment Feb 14 2026: the shadow BOLTS in Beat 3 and
// FUMBLES BOTH coins in his haste — the Blue Coin bag (500 Chits)
// AND a single Flame Coin (encircled by Mother Phoenix's harmless
// fire — the player's canonical first Gold Coin). Neither is a gift.
// Mother Phoenix's Beat-3 lines are intentional foreshadow that she
// recognises the fleeing figure. Never name him. (See canon file.)
const BEATS = [
  { id: "walk",     speaker: "???", line: "So you've woken. Take a step, child." },
  { id: "interact", speaker: "???", line: "The world speaks in light. Touch it." },
  { id: "combat",   speaker: "???", line: "Nothing here will hurt you. Learn the shape of a fight." },
  { id: "fumble",   speaker: "???", line: "…it dropped something. Take it. It was yours first, I think." },
  { id: "flame",    speaker: "???", line: "…and something brighter. Mother's fire remembers you." },
  { id: "exit",     speaker: "???", line: "Follow it if you dare. The above is waiting." },
];

function useTypewriter(text, speedMs = 32) {
  const [out, setOut] = useState("");
  useEffect(() => {
    setOut("");
    if (!text) return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) clearInterval(id);
    }, speedMs);
    return () => clearInterval(id);
  }, [text, speedMs]);
  return out;
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export default function TombOfAwakeningPage() {
  const navigate = useNavigate();
  const [pos, setPos] = useState(SPAWN);
  const [beat, setBeat] = useState(0);          // index into BEATS
  const [runeLit, setRuneLit] = useState(false);
  const [runeActivated, setRuneActivated] = useState(false);
  const [constructHp, setConstructHp] = useState(3);
  const [constructDefeated, setConstructDefeated] = useState(false);
  const [bagDropped, setBagDropped] = useState(false);   // shadow fumbled Blue-Coin bag
  const [bagTaken, setBagTaken] = useState(false);
  const [flameDropped, setFlameDropped] = useState(false); // shadow fumbled Flame Coin
  const [flameTaken, setFlameTaken] = useState(false);
  const [shadowVisible, setShadowVisible] = useState(true);
  const [shadowFleeing, setShadowFleeing] = useState(false);
  const [fadeIn, setFadeIn] = useState(true);
  const [walking, setWalking] = useState(false);
  const [target, setTarget] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const posRef = useRef(pos);

  useEffect(() => { posRef.current = pos; }, [pos]);

  // Load state — skip to end if already completed (replay ghost mode)
  useEffect(() => {
    axios.get(`${API}/tomb/state`).then(({ data }) => {
      if (data?.completed) setAlreadyCompleted(true);
    }).catch(() => {});
    const t = setTimeout(() => setFadeIn(false), 900);
    return () => clearTimeout(t);
  }, []);

  // Beat advancement
  const current = BEATS[beat];
  const typed = useTypewriter(current?.line, 28);

  // Beat 1 → advance on movement
  useEffect(() => {
    if (beat !== 0) return;
    if (dist(pos, SPAWN) > 60) {
      setBeat(1);
      setTimeout(() => setRuneLit(true), 500);
    }
  }, [pos, beat]);

  // Beat 2 → walk within proximity of rune, then advance
  useEffect(() => {
    if (beat !== 1 || !runeLit || runeActivated) return;
    if (dist(pos, RUNE) < RUNE.r + 20) {
      // Auto-activate on proximity for MVP simplicity
      setRuneActivated(true);
      setTimeout(() => setBeat(2), 800);
    }
  }, [pos, beat, runeLit, runeActivated]);

  // Beat 3 → walk over the fumbled Blue-Coin bag → pickup → advance to Beat 4
  useEffect(() => {
    if (beat !== 3 || bagTaken || !bagDropped) return;
    if (dist(pos, BAG) < BAG.r) {
      setBagTaken(true);
      toast.success("🪙 A coin-bag — dropped by the fleeing shadow");
      setTimeout(() => setBeat(4), 500);
    }
  }, [pos, beat, bagTaken, bagDropped]);

  // Beat 4 → walk over the Flame Coin → pickup → advance to Beat 5
  useEffect(() => {
    if (beat !== 4 || flameTaken || !flameDropped) return;
    if (dist(pos, FLAME_COIN) < FLAME_COIN.r) {
      setFlameTaken(true);
      toast.success("🔥 A Flame Coin — Mother Phoenix's harmless fire");
      setTimeout(() => setBeat(5), 500);
    }
  }, [pos, beat, flameTaken, flameDropped]);

  // Bag-land chime (WebAudio, no asset needed) — canonical "soft chime"
  // when the shadow fumbles the Blue-Coin bag onto the stone floor.
  useEffect(() => {
    if (!bagDropped) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTarget?.currentTime ?? ctx.currentTime;
      [880, 1320].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(freq, now + i * 0.05);
        g.gain.setValueAtTime(0.0001, now + i * 0.05);
        g.gain.exponentialRampToValueAtTime(0.12, now + 0.02 + i * 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.9 + i * 0.05);
        o.connect(g); g.connect(ctx.destination);
        o.start(now + i * 0.05);
        o.stop(now + 1.0 + i * 0.05);
      });
      setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1400);
    } catch { /* silent — audio is polish, not required */ void 0; }
  }, [bagDropped]);

  // Flame-coin chime — brighter, warmer, distinct from bag chime.
  useEffect(() => {
    if (!flameDropped) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTarget?.currentTime ?? ctx.currentTime;
      // Higher, longer, "brighter" ring
      [1568, 2093].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "triangle";
        o.frequency.setValueAtTime(freq, now + i * 0.06);
        g.gain.setValueAtTime(0.0001, now + i * 0.06);
        g.gain.exponentialRampToValueAtTime(0.1, now + 0.03 + i * 0.06);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 1.4 + i * 0.06);
        o.connect(g); g.connect(ctx.destination);
        o.start(now + i * 0.06);
        o.stop(now + 1.5 + i * 0.06);
      });
      setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1800);
    } catch { /* silent */ void 0; }
  }, [flameDropped]);

  // Combat resolution — defeating the construct startles the shadow.
  // The shadow bolts and fumbles BOTH coins mid-flight (canon Feb 14 2026):
  //   ~700ms → Blue-Coin bag lands
  //   ~900ms → Flame Coin lands (further along the arc, closer to exit)
  const strikeConstruct = () => {
    if (beat !== 2 || constructDefeated) return;
    const nextHp = Math.max(0, constructHp - 1);
    setConstructHp(nextHp);
    if (nextHp === 0) {
      setConstructDefeated(true);
      toast.success("✨ The construct dissolves");
      setTimeout(() => {
        setBeat(3);
        setShadowFleeing(true);
        setTimeout(() => setBagDropped(true),   700);
        setTimeout(() => setFlameDropped(true), 900);
        setTimeout(() => setShadowVisible(false), 1700);
      }, 800);
    }
  };

  // Movement — keyboard
  useEffect(() => {
    const onKey = (e) => {
      const key = e.key.toLowerCase();
      let dx = 0, dy = 0;
      if (key === "w" || key === "arrowup")    dy = -WALK_STEP;
      if (key === "s" || key === "arrowdown")  dy =  WALK_STEP;
      if (key === "a" || key === "arrowleft")  dx = -WALK_STEP;
      if (key === "d" || key === "arrowright") dx =  WALK_STEP;
      if (dx || dy) {
        e.preventDefault();
        setPos((p) => ({
          x: Math.max(SPRITE, Math.min(SCENE_W - SPRITE, p.x + dx)),
          y: Math.max(SPRITE + 60, Math.min(SCENE_H - SPRITE, p.y + dy)),
        }));
        setTarget(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Movement — click to walk (target-following animation)
  useEffect(() => {
    if (!target) return;
    setWalking(true);
    const id = setInterval(() => {
      const cur = posRef.current;
      const dx = target.x - cur.x;
      const dy = target.y - cur.y;
      const d = Math.hypot(dx, dy);
      if (d < CLICK_SPEED) {
        setPos(target);
        setTarget(null);
        setWalking(false);
        clearInterval(id);
        return;
      }
      const nx = cur.x + (dx / d) * CLICK_SPEED;
      const ny = cur.y + (dy / d) * CLICK_SPEED;
      setPos({
        x: Math.max(SPRITE, Math.min(SCENE_W - SPRITE, nx)),
        y: Math.max(SPRITE + 60, Math.min(SCENE_H - SPRITE, ny)),
      });
    }, 16);
    return () => clearInterval(id);
  }, [target]);

  const onSceneClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * SCENE_W;
    const y = ((e.clientY - rect.top) / rect.height) * SCENE_H;
    setTarget({ x, y });
  };

  const inExitZone =
    pos.x >= EXIT.x && pos.x <= EXIT.x + EXIT.w &&
    pos.y >= EXIT.y && pos.y <= EXIT.y + EXIT.h;

  const finish = async () => {
    setCompleting(true);
    try {
      const { data } = await axios.post(`${API}/tomb/complete`);
      if (data.first_time) {
        toast.success(
          `🕯️ +${data.chits_awarded} Chits · 🔥 Flame Coin — you've left the Tomb`
        );
      } else {
        toast.info("You've walked this floor before.");
      }
      setTimeout(() => navigate("/"), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't leave the Tomb");
      setCompleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-slate-100 flex items-center justify-center p-4"
         data-testid="tomb-of-awakening-page">
      <div className="w-full max-w-5xl">
        <div className="mb-2 flex items-baseline justify-between">
          <h1 className="font-serif text-2xl text-slate-300 tracking-wider">🕯️ Tomb of Awakening</h1>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            Tap or WASD to move
          </div>
        </div>

        <div
          data-testid="tomb-scene"
          onClick={onSceneClick}
          className="relative overflow-hidden rounded-2xl border border-slate-700 cursor-crosshair select-none"
          style={{
            width: "100%",
            aspectRatio: `${SCENE_W} / ${SCENE_H}`,
            background: [
              "radial-gradient(ellipse at 50% 0%, rgba(180,215,255,0.18) 0%, transparent 55%)",
              "radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.65) 0%, transparent 60%)",
              "linear-gradient(180deg, #0d1626 0%, #1a2036 40%, #171522 100%)",
            ].join(", "),
            boxShadow: "inset 0 0 120px rgba(0,0,0,0.75)",
          }}
        >
          {/* SVG playfield */}
          <svg viewBox={`0 0 ${SCENE_W} ${SCENE_H}`} preserveAspectRatio="xMidYMid slice"
               className="absolute inset-0 h-full w-full">
            {/* Moonlight beam */}
            <defs>
              <linearGradient id="moon" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(180,215,255,0.55)" />
                <stop offset="100%" stopColor="rgba(180,215,255,0)" />
              </linearGradient>
              <radialGradient id="runeGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(96,180,255,0.9)" />
                <stop offset="100%" stopColor="rgba(96,180,255,0)" />
              </radialGradient>
            </defs>
            <polygon points={`${SCENE_W/2 - 90},0 ${SCENE_W/2 + 90},0 ${SCENE_W/2 + 45},${SCENE_H} ${SCENE_W/2 - 45},${SCENE_H}`}
                     fill="url(#moon)" opacity="0.55" />

            {/* Blue-ether runes on the walls (pulse) */}
            {[
              { x: RUNE.x, y: RUNE.y, primary: true },
              { x: 460, y: 100 },
              { x: 780, y: 120 },
              { x: 60, y: 420 },
              { x: 500, y: 470 },
            ].map((r, i) => (
              <g key={i}>
                <circle cx={r.x} cy={r.y} r="46"
                        fill="url(#runeGlow)"
                        opacity={r.primary && runeLit ? (runeActivated ? 0.4 : 0.9) : 0.15}>
                  <animate attributeName="opacity"
                           values={r.primary && runeLit ? "0.6;1;0.6" : "0.1;0.25;0.1"}
                           dur={r.primary && runeLit ? "1.4s" : "3.2s"}
                           repeatCount="indefinite" />
                </circle>
                <text x={r.x} y={r.y + 6} textAnchor="middle"
                      fontSize="20" fill="rgba(150,200,255,0.7)"
                      style={{ fontFamily: "serif" }}>
                  {["ᛝ","ᛟ","ᛞ","ᛁ","ᛉ"][i] || "ᛁ"}
                </text>
              </g>
            ))}

            {/* Exit doorway */}
            <rect x={EXIT.x} y={EXIT.y} width={EXIT.w} height={EXIT.h}
                  fill="rgba(20,10,30,0.85)"
                  stroke="rgba(180,215,255,0.4)"
                  strokeWidth="2"
                  rx="8" />
            <text x={EXIT.x + EXIT.w / 2} y={EXIT.y + EXIT.h / 2}
                  textAnchor="middle" fontSize="10"
                  fill="rgba(180,215,255,0.5)"
                  letterSpacing="4">
              ABOVE
            </text>

            {/* Coin-bag — only appears AFTER the shadow fumbles it mid-flight.
                Canon: dropped by the fleeing shadow, not a placed reward. */}
            {bagDropped && !bagTaken && (
              <g data-testid="tomb-bag"
                 style={{ animation: "tombBagLand 500ms cubic-bezier(0.4,0,0.6,1.4) 0ms both" }}>
                {/* landing dust puff */}
                <ellipse cx={BAG.x} cy={BAG.y + 14} rx="18" ry="4"
                         fill="rgba(180,215,255,0.25)">
                  <animate attributeName="rx" values="4;22;16" dur="600ms" repeatCount="1" />
                  <animate attributeName="opacity" values="0.6;0.3;0" dur="900ms" repeatCount="1" fill="freeze" />
                </ellipse>
                {/* bag body — patched cloth with drawstring */}
                <ellipse cx={BAG.x} cy={BAG.y + 6} rx="16" ry="12"
                         fill="#3a2a1a" stroke="#6d4a20" strokeWidth="1.5" />
                <path d={`M ${BAG.x - 8} ${BAG.y - 4} Q ${BAG.x} ${BAG.y - 12} ${BAG.x + 8} ${BAG.y - 4}`}
                      fill="none" stroke="#6d4a20" strokeWidth="2" />
                {/* Dragonious Blue Coins glinting from top */}
                <circle cx={BAG.x - 4} cy={BAG.y - 3} r="4"
                        fill="rgba(96,180,255,0.95)" stroke="rgba(220,240,255,0.9)" strokeWidth="1">
                  <animate attributeName="opacity" values="0.7;1;0.7" dur="1.4s" repeatCount="indefinite" />
                </circle>
                <circle cx={BAG.x + 5} cy={BAG.y - 2} r="3.5"
                        fill="rgba(96,180,255,0.85)" stroke="rgba(220,240,255,0.9)" strokeWidth="1">
                  <animate attributeName="opacity" values="1;0.6;1" dur="1.6s" repeatCount="indefinite" />
                </circle>
                <text x={BAG.x} y={BAG.y + 10} textAnchor="middle"
                      fontSize="9" fill="rgba(96,180,255,0.7)"
                      letterSpacing="1">500 Ċ</text>
              </g>
            )}

            {/* Flame Coin — the canonical FIRST Gold Coin (Mother
                Phoenix's harmless fire). Also fumbled by the shadow,
                lands further along the arc, closer to the exit.
                Canon: single, one-time, key-item — NOT a bag/stack. */}
            {flameDropped && !flameTaken && (
              <g data-testid="tomb-flame-coin"
                 style={{ animation: "tombFlameLand 700ms cubic-bezier(0.4,0,0.6,1.4) 0ms both" }}>
                <defs>
                  <radialGradient id={`flameHalo-${FLAME_COIN.x}`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%"   stopColor="rgba(255,190,90,0.95)" />
                    <stop offset="45%"  stopColor="rgba(255,120,40,0.55)" />
                    <stop offset="100%" stopColor="rgba(255,90,20,0)" />
                  </radialGradient>
                </defs>
                {/* Harmless-fire halo (pulsing) */}
                <circle cx={FLAME_COIN.x} cy={FLAME_COIN.y} r="28"
                        fill={`url(#flameHalo-${FLAME_COIN.x})`}>
                  <animate attributeName="r" values="22;30;22" dur="1.8s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;1;0.8" dur="1.8s" repeatCount="indefinite" />
                </circle>
                {/* Coin body — gold with fire-flecked edge */}
                <circle cx={FLAME_COIN.x} cy={FLAME_COIN.y} r="11"
                        fill="#f6c247" stroke="#c88024" strokeWidth="2" />
                <circle cx={FLAME_COIN.x} cy={FLAME_COIN.y} r="7"
                        fill="none" stroke="rgba(255,220,120,0.9)" strokeWidth="1" />
                <text x={FLAME_COIN.x} y={FLAME_COIN.y + 5} textAnchor="middle"
                      fontSize="14" fill="#7a3d0a" fontWeight="bold"
                      style={{ fontFamily: "serif" }}>🔥</text>
                <text x={FLAME_COIN.x} y={FLAME_COIN.y + 28} textAnchor="middle"
                      fontSize="9" fill="rgba(255,200,110,0.9)"
                      letterSpacing="2">FLAME COIN</text>
              </g>
            )}

            {/* Blue-ether construct — appears on combat beat */}
            {beat === 2 && !constructDefeated && (
              <g
                data-testid="tomb-construct"
                style={{ cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); strikeConstruct(); }}
              >
                <circle cx={CONSTRUCT.x} cy={CONSTRUCT.y} r={30 + constructHp * 6}
                        fill="rgba(96,180,255,0.2)"
                        stroke="rgba(96,180,255,0.9)"
                        strokeWidth="2">
                  <animate attributeName="r" values={`${25 + constructHp * 6};${35 + constructHp * 6};${25 + constructHp * 6}`}
                           dur="1.6s" repeatCount="indefinite" />
                </circle>
                <text x={CONSTRUCT.x} y={CONSTRUCT.y + 6} textAnchor="middle"
                      fontSize="22" fill="rgba(180,215,255,0.95)">✦</text>
                <text x={CONSTRUCT.x} y={CONSTRUCT.y - 45} textAnchor="middle"
                      fontSize="10" fill="rgba(180,215,255,0.7)"
                      letterSpacing="2">
                  HP {constructHp} · TAP TO STRIKE
                </text>
              </g>
            )}

            {/* Shadow — watches from the corner, then BOLTS toward exit
                (fumbling the bag mid-flight — canon Feb 14 2026).
                Silhouette only — NEVER name him. */}
            {shadowVisible && (
              <g data-testid="tomb-shadow"
                 opacity={shadowFleeing ? 0.45 : 0.75}
                 style={{
                   transform: shadowFleeing
                     ? `translate(${EXIT.x + EXIT.w/2 - SHADOW_START.x}px, ${EXIT.y + 60 - SHADOW_START.y}px)`
                     : "translate(0,0)",
                   transition: "transform 1.6s cubic-bezier(0.4, 0.0, 0.9, 0.6)",
                 }}>
                <ellipse cx={SHADOW_START.x} cy={SHADOW_START.y} rx="22" ry="30" fill="rgba(10,0,20,0.8)" />
                <ellipse cx={SHADOW_START.x} cy={SHADOW_START.y} rx="14" ry="22" fill="rgba(30,0,40,0.9)" />
              </g>
            )}

            {/* Character sprite */}
            <g data-testid="tomb-character"
               transform={`translate(${pos.x - SPRITE / 2}, ${pos.y - SPRITE / 2})`}>
              <ellipse cx={SPRITE / 2} cy={SPRITE + 6} rx={SPRITE / 3} ry="4"
                       fill="rgba(0,0,0,0.5)" />
              <circle cx={SPRITE / 2} cy={SPRITE / 2 - 4} r="10"
                      fill="#f7d9b2" />
              <rect x={SPRITE / 2 - 8} y={SPRITE / 2 + 4} width="16" height="18"
                    rx="4" fill="#6D28D9" />
              {walking && (
                <circle cx={SPRITE / 2} cy={SPRITE + 4} r="3" fill="rgba(255,255,255,0.4)">
                  <animate attributeName="opacity" values="0;1;0" dur="0.4s" repeatCount="indefinite" />
                </circle>
              )}
            </g>
          </svg>

          {/* Fade-in overlay */}
          {fadeIn && (
            <div className="absolute inset-0 bg-black pointer-events-none"
                 style={{ animation: "tombFadeOut 900ms ease-out forwards" }} />
          )}

          {/* Exit prompt — appears once player has picked up BOTH coins
              (Blue-Coin bag AND Flame Coin — canonical arcade key pair). */}
          {beat === 5 && bagTaken && flameTaken && inExitZone && (
            <button
              data-testid="tomb-exit-btn"
              onClick={(e) => { e.stopPropagation(); finish(); }}
              disabled={completing}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full border border-blue-400/60 bg-slate-900/90 px-6 py-2.5 text-sm font-bold text-blue-100 shadow-[0_0_30px_rgba(96,180,255,0.5)] hover:bg-slate-800 disabled:opacity-50"
            >
              {completing ? "Leaving…" : "Leave the Tomb →"}
            </button>
          )}
        </div>

        {/* Dialog bubble */}
        <div
          data-testid="tomb-dialog"
          className="mt-4 rounded-xl border border-blue-500/30 bg-gradient-to-b from-slate-900 to-slate-950 p-4 shadow-[0_0_40px_rgba(96,180,255,0.15)]"
          style={{ minHeight: 84 }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-300/70 mb-1">
            {current?.speaker}
          </div>
          <div className="text-lg text-blue-50/90 italic leading-relaxed">
            {typed}
            <span className="animate-pulse text-blue-300">▍</span>
          </div>
          {alreadyCompleted && beat === 0 && (
            <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-500">
              👣 Replay mode — no rewards
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes tombFadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes tombBagLand {
          0%   { transform: translate(180px, -80px) rotate(-15deg); opacity: 0; }
          40%  { transform: translate(60px, -20px) rotate(-5deg); opacity: 1; }
          70%  { transform: translate(10px, 6px) rotate(2deg); }
          100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes tombFlameLand {
          0%   { transform: translate(60px, -120px) scale(0.4); opacity: 0; }
          30%  { transform: translate(30px, -40px)  scale(0.7); opacity: 1; }
          70%  { transform: translate(6px, 8px)     scale(1.1); }
          100% { transform: translate(0, 0)         scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
}
